import { forwardRef, useState } from 'react';

import {
  CloseButton,
  Combobox,
  Highlight,
  ScrollArea,
  useCombobox
} from '@mantine/core';
import type { ScrollAreaAutosizeProps, TextareaProps } from '@mantine/core';

import { Textarea } from '../textarea';

import classes from './Autocomplete.module.css';

// hacky!
interface ComboboxAutoCompleteProps extends Omit<TextareaProps, 'onChange'> {
  data: string[];
  searchable?: boolean;
  scrollContainerHeight?: ScrollAreaAutosizeProps['h'];
  scrollType?: ScrollAreaAutosizeProps['type'];
  value?: string;
  onChange?: (value: string) => void;
  allowOnlyPresets?: boolean;
}

/**
 *
 * @returns React.ReactNode - A combobox based autocomplete with textarea input
 */
const ComboboxAutoComplete = forwardRef<
  HTMLTextAreaElement,
  ComboboxAutoCompleteProps
>(
  (
    {
      data,
      value,
      onChange,
      scrollContainerHeight = 300,
      scrollType = 'auto',
      allowOnlyPresets = false,
      searchable,
      ...rest
    },
    ref
  ) => {
    const combobox = useCombobox({
      onDropdownClose: () => combobox.resetSelectedOption()
    });
    const [_value, setValue] = useState(value || '');
    const shouldFilterOptions =
      allowOnlyPresets || searchable ? !data.some((item) => item === _value) : false;
    const filteredOptions = shouldFilterOptions
      ? data.filter((item) =>
          item.toLowerCase().includes(_value.toLowerCase().trim())
        )
      : data;

    const options = filteredOptions.map((item, idx) => (
      <Combobox.Option
        value={item}
        key={idx}
        className={classes.autocompleteOptions}
      >
        <Highlight highlight={_value}>{item}</Highlight>
      </Combobox.Option>
    ));

    return (
      <Combobox
        onOptionSubmit={(optionValue) => {
          setValue(optionValue);
          onChange && onChange(optionValue);
          combobox.closeDropdown();
        }}
        onClose={() => {
          onChange && onChange(_value);
          combobox.closeDropdown();
        }}
        store={combobox}
        withinPortal={false}
      >
        <Combobox.Target>
          <Textarea
            {...rest}
            ref={ref}
            value={_value}
            onChange={(event) => {
              setValue(event.currentTarget.value);
              combobox.updateSelectedOptionIndex();
              combobox.openDropdown();
            }}
            onClick={() => combobox.openDropdown()}
            onFocus={() => combobox.openDropdown()}
            onBlur={() => combobox.closeDropdown()}
            rightSection={
              _value !== '' && (
                <CloseButton
                  size="sm"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => setValue('')}
                  aria-label="Clear value"
                />
              )
            }
          />
        </Combobox.Target>

        <Combobox.Dropdown>
          <Combobox.Options>
            <ScrollArea.Autosize mah={scrollContainerHeight} type={scrollType}>
              {options.length === 0 ? (
                <Combobox.Empty>Nothing found</Combobox.Empty>
              ) : (
                options
              )}
            </ScrollArea.Autosize>
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    );
  }
);

export { ComboboxAutoComplete };
